// The picture-in-picture body: a small mirror of the meeting room — the people
// as compact tiles, the shared screen under them when someone presents, and an
// island control bar (mic · camera · share · more · leave) styled like the web
// view's round controls.
//
// Mounted into its OWN React root inside the PiP window (NOT a portal): React
// attaches its event listeners to the container's document, so a portal from the
// main window would render fine but its buttons would never receive clicks — the
// events fire in the PiP document where the main tree has no listener. A separate
// root fixes that. Its handlers still run in the MAIN window's realm (the root is
// created there), so module singletons — the stores and `engine` — and even
// `window.focus()` (to jump back to the meeting tab) work; it just has no router,
// so leaving is a plain engine.disconnect() that the meeting page reacts to.
//
// The tiles/screen attach the SAME decrypted tracks as the main grid — LiveKit
// tracks attach to more than one element — so nothing is re-encoded and E2EE is
// untouched; audio keeps playing from the main window's <RemoteAudio>, so the
// tiles stay muted.

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { engine } from '../engine';
import { closePip, toggleMinimize, usePipStore } from '../store/pipStore';
import { useCallStore, useCallStoreShallow } from '../store/callStore';
import { Avatar } from '../ui/Avatar';
import { IconButton } from '../ui/IconButton';
import {
  IconCamera,
  IconCameraOff,
  IconDots,
  IconFullScreen,
  IconLeave,
  IconMicOff,
  IconMicrophone,
  IconMinimize,
  IconShareScreen,
} from '../ui/icons';

/** One person, compact: their camera if it is on, else a small avatar, with the
 * name and a mic-off dot. A trimmed ParticipantTile — the PiP is a glance, not
 * the room, so it drops the pin/quality/equalizer chrome. */
const PipTile = memo(function PipTile({ identity }: { identity: string }) {
  const attendee = useCallStore((s) => s.attendees[identity]);
  const trackEpoch = useCallStore((s) => s.trackEpochs[identity] ?? 0);
  // Only the local participant's role is known client-side (there is no
  // per-remote-attendee role in the live state), so the host ring shows on the
  // host's OWN tile — which is the case that matters when the host pops out.
  const amHost = useCallStore((s) => s.isHost);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasVideo = !!attendee?.cameraEnabled;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !hasVideo) return;
    const track = engine.videoTrack(identity, 'camera');
    if (!track) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [identity, hasVideo, trackEpoch]);

  if (!attendee) return null;
  return (
    <div
      className={`bz-pip-tile${attendee.isSpeaking ? ' bz-pip-tile-speaking' : ''}${
        attendee.isLocal && amHost ? ' bz-pip-tile-host' : ''
      }`}
    >
      {hasVideo ? (
        <video
          ref={videoRef}
          className={`bz-pip-video${attendee.isLocal ? ' bz-tile-mirror' : ''}`}
          autoPlay
          playsInline
          muted
        />
      ) : (
        <Avatar
          name={attendee.name}
          identity={identity}
          isSelf={attendee.isLocal}
          size={30}
          photoUrl={attendee.avatarUrl}
        />
      )}
      <span className="bz-pip-name">
        {!attendee.micEnabled && <IconMicOff size={8} />}
        <span className="bz-pip-name-text">
          {attendee.name}
          {attendee.isLocal ? ' (You)' : ''}
        </span>
      </span>
    </div>
  );
});

/** The shared screen (F-CALL-2), shown under the people while someone presents.
 * Contained, not cropped — a shared screen with a corner cut off is useless. */
const PipScreen = memo(function PipScreen({ identity }: { identity: string }) {
  const trackEpoch = useCallStore((s) => s.trackEpochs[identity] ?? 0);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const track = engine.videoTrack(identity, 'screen');
    if (!track) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [identity, trackEpoch]);
  return <video ref={videoRef} className="bz-pip-screen" autoPlay playsInline muted />;
});

/** The island control bar — the web view's round controls, shrunk: mic, camera,
 * share, a more menu (raise hand / back to the meeting tab), and leave. */
const PipControls = memo(function PipControls() {
  const [moreOpen, setMoreOpen] = useState(false);
  const minimized = usePipStore((s) => s.minimized);
  const local = useCallStoreShallow((s) => {
    const a = s.localIdentity ? s.attendees[s.localIdentity] : undefined;
    return a
      ? { mic: a.micEnabled, cam: a.cameraEnabled, share: a.screenShareEnabled, hand: a.handRaisedAt !== null }
      : undefined;
  });
  const toggleMic = useCallback(() => void engine.setMicEnabled(!local?.mic), [local?.mic]);
  const toggleCam = useCallback(() => void engine.setCameraEnabled(!local?.cam), [local?.cam]);
  const toggleShare = useCallback(
    () => void engine.setScreenShareEnabled(!local?.share).catch(() => undefined),
    [local?.share],
  );
  const toggleMore = useCallback(() => setMoreOpen((v) => !v), []);
  const raiseHand = useCallback(() => {
    setMoreOpen(false);
    void engine.setHandRaised(!local?.hand);
  }, [local?.hand]);
  const backToMeeting = useCallback(() => {
    setMoreOpen(false);
    window.focus(); // handlers run in the main window's realm — focus the tab
    closePip();
  }, []);
  const leave = useCallback(() => {
    closePip();
    void engine.disconnect(); // the meeting page's disconnect watcher navigates
  }, []);

  return (
    <div className="bz-pip-bar">
      {moreOpen && (
        <div className="bz-pip-menu" role="menu">
          <button type="button" className="bz-pip-menu-item" onClick={raiseHand}>
            <span className="bz-pip-menu-emoji">✋</span>
            {local?.hand ? 'Lower hand' : 'Raise hand'}
          </button>
          <button type="button" className="bz-pip-menu-item" onClick={backToMeeting}>
            <IconFullScreen size={14} />
            Back to meeting
          </button>
        </div>
      )}
      <IconButton
        className="bz-pip-min-btn"
        label={minimized ? 'Expand' : 'Minimize'}
        tone={minimized ? 'active' : 'default'}
        onClick={toggleMinimize}
      >
        {minimized ? <IconFullScreen size={16} /> : <IconMinimize size={18} />}
      </IconButton>
      <span className="bz-pip-bar-divider" aria-hidden="true" />
      <IconButton
        label={local?.mic ? 'Mute' : 'Unmute'}
        tone={local?.mic ? 'default' : 'danger'}
        onClick={toggleMic}
      >
        {local?.mic ? <IconMicrophone size={17} /> : <IconMicOff size={17} />}
      </IconButton>
      <IconButton
        label={local?.cam ? 'Turn camera off' : 'Turn camera on'}
        tone={local?.cam ? 'default' : 'danger'}
        onClick={toggleCam}
      >
        {local?.cam ? <IconCamera size={17} /> : <IconCameraOff size={17} />}
      </IconButton>
      <IconButton
        label={local?.share ? 'Stop sharing' : 'Share screen'}
        tone={local?.share ? 'active' : 'default'}
        onClick={toggleShare}
      >
        <IconShareScreen size={17} />
      </IconButton>
      <IconButton label="More options" tone={moreOpen ? 'active' : 'default'} onClick={toggleMore}>
        <IconDots size={17} />
      </IconButton>
      <IconButton label="Leave" tone="danger" onClick={leave}>
        <IconLeave size={17} />
      </IconButton>
    </div>
  );
});

/** The compact grid (or people-strip + shared screen while presenting) over the
 * control island. Rendered into the PiP window's own React root; updates come
 * from its store subscriptions, not the main tree. */
const PipContent = memo(function PipContent() {
  const order = useCallStoreShallow((s) => s.order);
  const minimized = usePipStore((s) => s.minimized);
  const presenterId = useCallStoreShallow(
    (s) => s.order.find((id) => s.attendees[id]?.screenShareEnabled) ?? null,
  );
  return (
    <div className={minimized ? 'bz-pip bz-pip-min' : 'bz-pip'}>
      {presenterId ? (
        <>
          <div className="bz-pip-strip">
            {order.map((id) => (
              <PipTile key={id} identity={id} />
            ))}
          </div>
          <div className="bz-pip-stage">
            <PipScreen identity={presenterId} />
          </div>
        </>
      ) : (
        <div className="bz-pip-grid">
          {order.map((id) => (
            <PipTile key={id} identity={id} />
          ))}
        </div>
      )}
      <PipControls />
    </div>
  );
});

/** Mounted by CallView. While the PiP window is open, it owns a React root inside
 * that window and renders the compact meeting there; it renders nothing itself. */
export function MeetingPiP() {
  const pipWindow = usePipStore((s) => s.window);
  useEffect(() => {
    if (!pipWindow) return;
    const root = createRoot(pipWindow.document.body);
    root.render(<PipContent />);
    return () => {
      // The window may already be gone (the user closed the PiP chrome); tearing
      // down a detached root is harmless but guard the rare throw anyway.
      try {
        root.unmount();
      } catch {
        /* window already closed */
      }
    };
  }, [pipWindow]);
  return null;
}
